// ⭐ Premium Areluxe 3D Black–Gold Payouts Methods (Web Version)

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store";

import {
  ChevronLeft,
  Building2,
  CreditCard,
  Wallet,
  Circle,
  CheckCircle2,
} from "lucide-react";

const gold = "#bf9b30";

const PayoutsMethods: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();

  // USER ID: params > auth
  const paramUserId = params.userId;
  const authUser = useSelector((s: RootState) => s.auth.user);
  const authUserId = authUser?.userId;

  const userId = paramUserId || authUserId;

  // Redirect safely
  useEffect(() => {
    if (!userId) navigate("/AuthOverview");
  }, [userId, navigate]);

  const [selected, setSelected] =
    useState<"paypal" | "payoneer" | "wire" | "">("");

  const handleContinue = () => {
    if (!selected || !userId) return;

    if (selected === "paypal") navigate(`/PaypalOnboarding/${userId}`);
    if (selected === "payoneer") navigate(`/PayoneerOnboarding/${userId}`);
    if (selected === "wire") navigate(`/WireTransferOnboarding/${userId}`);
  };

  /* ---- PREMIUM CARD COMPONENT ---- */
  const OptionCard = ({
    id,
    icon,
    label,
    subtitle,
  }: {
    id: "paypal" | "payoneer" | "wire";
    label: string;
    subtitle: string;
    icon: React.ReactNode;
  }) => (
    <div
      onClick={() => setSelected(id)}
      style={{
        background: "linear-gradient(145deg,#0c0c0c,#161616)",
        padding: 20,
        borderRadius: 16,
        cursor: "pointer",
        width: "100%",
        maxWidth: 500,
        marginBottom: 18,
        border:
          selected === id
            ? `2px solid ${gold}`
            : "1px solid rgba(191,155,48,0.25)",
        boxShadow:
          selected === id
            ? "0 0 18px rgba(191,155,48,0.25), inset 0 0 12px rgba(191,155,48,0.15)"
            : "0 4px 12px rgba(0,0,0,0.4)",
        transition: "all 0.25s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ minWidth: 28 }}>{icon}</div>

        <div style={{ flex: 1 }}>
          <div style={styles.cardTitle}>{label}</div>
          <div style={styles.cardSubtitle}>{subtitle}</div>
        </div>

        {selected === id ? (
          <CheckCircle2 size={24} color={gold} />
        ) : (
          <Circle size={22} color="#666" />
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        background: "linear-gradient(180deg,#0a0a0a,#111)",
        minHeight: "100vh",
        paddingTop: 30,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 20,
        color: "#fff",
      }}
    >
      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "none",
          border: "none",
          padding: 8,
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        <ChevronLeft size={28} color={gold} />
      </button>

      {/* CONTENT */}
      <div style={{ width: "100%", maxWidth: 500 }}>
        <div style={styles.header}>How would you like to get paid?</div>
        <div style={styles.sub}>Payouts are processed in EUR.</div>

        {/* ---- OPTIONS ---- */}
        <OptionCard
          id="wire"
          label="International Wire Transfer"
          subtitle="3–7 business days • Bank fees may apply"
          icon={<Building2 size={28} color={gold} />}
        />

  {/*
  
      <OptionCard
          id="payoneer"
          label="Payoneer"
          subtitle="Mastercard • Up to 24h • Payoneer fees may apply"
          icon={<CreditCard size={28} color="#ff6600" />}
        />

        <OptionCard
          id="paypal"
          label="PayPal"
          subtitle="1 business day • PayPal fees may apply"
          icon={<Wallet size={28} color="#009cde" />}
        />



  
  */}

        <div style={styles.footerText}>
          Processing times vary depending on your bank and region.
        </div>

        {/* ---- CONTINUE BUTTON ---- */}
        <button
          disabled={!selected}
          onClick={handleContinue}
          style={{
            background:
              "linear-gradient(90deg,#1a1a1a,#000,#1a1a1a,#000)",
            padding: "14px 0",
            width: "100%",
            borderRadius: 14,
            marginTop: 26,
            fontSize: 17,
            fontWeight: "800",
            color: gold,
            border: "1px solid rgba(191,155,48,0.4)",
            boxShadow: selected
              ? "0 5px 15px rgba(191,155,48,0.25)"
              : "0 2px 8px rgba(0,0,0,0.35)",
            cursor: selected ? "pointer" : "not-allowed",
            opacity: selected ? 1 : 0.4,
            maxWidth: 500,
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default PayoutsMethods;

/* --------------------------------- STYLES --------------------------------- */
const styles: Record<string, React.CSSProperties> = {
  header: {
    fontSize: 22,
    fontWeight: 800,
    marginBottom: 8,
    color: "#fff",
  },

  sub: {
    color: "#bbb",
    fontSize: 14,
    marginBottom: 22,
  },

  cardTitle: {
    color: "#fff",
    fontSize: 16,
    fontWeight: 600,
  },

  cardSubtitle: {
    color: "#777",
    fontSize: 12,
    marginTop: 3,
  },

  footerText: {
    color: "#777",
    textAlign: "center",
    fontSize: 12,
    marginTop: 18,
  },
};
